import React, { useState } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_OverridingVariables() {
  const [theme, setTheme] = useState("light");
  const [buttonHovered, setButtonHovered] = useState(false);

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 Advanced CSS: Overriding Variables</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS variables (custom properties) can be overridden in various scopes:
        globally, at component level, in media queries, or even dynamically via
        JavaScript. This allows highly flexible theming and responsive designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#fff7ed,#ffe4b5)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#9a3412" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Global variables are defined in <code>:root</code>.
          </li>
          <li>
            Component-level variables can override global ones for isolated
            styling.
          </li>
          <li>
            Variables can be overridden inside media queries for responsive
            design.
          </li>
          <li>
            Pseudo-classes like <code>:hover</code> can temporarily override
            variables.
          </li>
          <li>JavaScript can dynamically override variables at runtime.</li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">📝 Examples of Overriding Variables</h2>

        {/* 1. Global Variables */}
        <h3>1. Global Variables</h3>
        <CodeCopyBlock
          code={`:root {
  --primary-color: #3b82f6;
  --secondary-color: #9333ea;
  --btn-padding: 10px 20px;
}`}
        />
        <div
          style={{
            background: "var(--primary-color,#3b82f6)",
            color: "white",
            padding: "var(--btn-padding,10px 20px)",
            borderRadius: "8px",
            display: "inline-block",
            marginBottom: "1rem",
          }}
        >
          Global Button
        </div>

        {/* 2. Component-level Overrides */}
        <h3>2. Component-level Overrides</h3>
        <CodeCopyBlock
          code={`.card {
  --primary-color: #10b981; /* overrides global */
  background: var(--primary-color);
  padding: 20px;
  border-radius: 12px;
}`}
        />
        <div
          style={{
            "--primary-color": "#10b981",
            background: "var(--primary-color,#3b82f6)",
            padding: "20px",
            borderRadius: "12px",
            color: "white",
            marginBottom: "1rem",
          }}
        >
          Card with component-level override
        </div>

        {/* 3. Media Query Overrides */}
        <h3>3. Media Query / Responsive Overrides</h3>
        <CodeCopyBlock
          code={`:root {
  --btn-bg: #3b82f6;
}
@media(min-width:768px){
  :root { --btn-bg: #9333ea; }
}
.button {
  background: var(--btn-bg);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
}`}
        />
        <div
          style={{
            background: "var(--btn-bg,#3b82f6)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            marginBottom: "1rem",
          }}
        >
          Responsive Button (changes with screen width)
        </div>

        {/* 4. Pseudo-class Overrides */}
        <h3>4. Hover / Active Overrides</h3>
        <CodeCopyBlock
          code={`.btn {
  --btn-color: #fff;
  background: #3b82f6;
  color: var(--btn-color);
  padding: 10px 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
}
.btn:hover {
  --btn-color: #fbbf24;
  transform: scale(1.05);
}`}
        />
        <div
          style={{
            background: "#3b82f6",
            color: buttonHovered ? "#fbbf24" : "#fff",
            padding: "10px 20px",
            borderRadius: "8px",
            display: "inline-block",
            cursor: "pointer",
            transition: "all 0.3s ease",
            marginBottom: "1rem",
          }}
          onMouseEnter={() => setButtonHovered(true)}
          onMouseLeave={() => setButtonHovered(false)}
        >
          Hover Me
        </div>

        {/* 5. Dynamic JS Overrides */}
        <h3>5. Dynamic JavaScript Overrides</h3>
        <CodeCopyBlock
          code={`// Change CSS variable at runtime
document.documentElement.style.setProperty('--primary-color','#10b981');`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "2rem" }}>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              background: "var(--primary-color,#3b82f6)",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
            onClick={() =>
              document.documentElement.style.setProperty(
                "--primary-color",
                "#9333ea"
              )
            }
          >
            Switch Primary Color
          </button>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              background: "var(--primary-color,#3b82f6)",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
            onClick={() =>
              document.documentElement.style.setProperty(
                "--primary-color",
                "#3b82f6"
              )
            }
          >
            Reset Primary Color
          </button>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#fff7ed,#ffe4b5)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#9a3412" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine pseudo-class overrides with component-level variables for
            interactive UI.
          </li>
          <li>
            Use JS overrides to implement theme switchers and dynamic
            dashboards.
          </li>
          <li>
            Experiment with media-query variable overrides for fully responsive
            designs.
          </li>
          <li>
            Mix gradients, shadows, and animations using variables for advanced
            customization.
          </li>
        </ul>
      </div>
    </div>
  );
}
